import { MailPlusIcon } from "lucide-react";
import { useState } from "react";
import { z } from "zod";

import { trpc } from "@blis/trpc/react";

import { SearchableSelect } from "@/components/ui/searchable-select";
import { Text } from "@/components/ui/text";

type EmailSelectProps = {
  value: string[];
  onValueChange: (value: string[]) => void;
  disabled?: boolean;
  inputRef?: React.Ref<HTMLInputElement>;
  placeholder?: string;
};

export function EmailSelect({
  value,
  onValueChange,
  inputRef,
  placeholder = "None",
}: EmailSelectProps) {
  const createKey = "__create_email__";
  const [input, setInput] = useState("");
  const [created, setCreated] = useState<string[]>([]);
  const emails = trpc.accession.recentEmails.useQuery();
  const [error, setError] = useState<string | null>(null);
  const selectedKeys = new Set(value);
  const optionItems = Array.from(
    new Set([...(emails.data || []), ...created]),
  ).map((email) => ({ id: email, label: email }));

  const onEmailCreate = (emailStr: string) => {
    const parsed = z.email().safeParse(emailStr);
    if (parsed.success) {
      setError(null);
      setCreated((prev) => [...prev, parsed.data]);
      setInput("");
      return parsed.data;
    }
    setError("Invalid email");
    return null;
  };

  return (
    <SearchableSelect
      selectionMode="multiple"
      selectedKeys={selectedKeys}
      onSelectionChange={(keys) => {
        if (keys === "all") {
          onValueChange(optionItems.map((item) => item.id));
          return;
        }
        const allKeys = Array.from(keys);
        const pickedCreate = allKeys.includes(createKey);
        const next = allKeys.filter(
          (key): key is string => typeof key === "string",
        );
        const nextWithoutCreate = next.filter((key) => key !== createKey);

        if (pickedCreate && input.trim()) {
          const createdEmail = onEmailCreate(input.trim());
          if (createdEmail) {
            onValueChange(
              Array.from(new Set([...nextWithoutCreate, createdEmail])),
            );
            return;
          }
        }

        onValueChange(nextWithoutCreate);
      }}
    >
      <SearchableSelect.Button className="justify-between">
        <SearchableSelect.Value
          items={optionItems}
          getKey={(item) => item.id}
          getText={(item) => item.label}
          placeholder={placeholder}
          className="flex-1 text-start"
        >
          {({ selectedItems }) => (
            <span className="inline-flex min-w-0 items-center">
              <span className="truncate">
                {selectedItems.length === 1
                  ? selectedItems[0].label
                  : `${selectedItems.length} emails`}
              </span>
            </span>
          )}
        </SearchableSelect.Value>
      </SearchableSelect.Button>
      <SearchableSelect.Popover placement="bottom end">
        <SearchableSelect.Input
          ref={inputRef}
          placeholder="Search or create email..."
          value={input}
          onChange={(event) => {
            setInput(event.currentTarget.value);
            if (error) setError(null);
          }}
        />
        {emails.isPending && (
          <div className="px-3 py-2 text-muted-foreground text-xs">
            Loading emails...
          </div>
        )}
        {!emails.isPending && (
          <div className="px-3 py-2 text-muted-foreground text-xs flex items-center gap-2">
            Search existing emails or add a new one
          </div>
        )}
        {error && (
          <Text className="inline-block w-full p-2 bg-red-50 text-destructive">
            {error}
          </Text>
        )}
        <SearchableSelect.List
          renderEmptyState={() => (
            <Text muted size="1" className="p-2">
              No emails found
            </Text>
          )}
        >
          {optionItems.map((item) => (
            <SearchableSelect.MultiItem
              key={item.id}
              id={item.id}
              textValue={item.label}
            >
              {item.label}
            </SearchableSelect.MultiItem>
          ))}
          {input.trim() && (
            <SearchableSelect.Item id={createKey} textValue={input.trim()}>
              <div className="flex items-center gap-2">
                <MailPlusIcon className="size-4" />
                <Text bold truncate>
                  {input.trim()}
                </Text>
              </div>
            </SearchableSelect.Item>
          )}
        </SearchableSelect.List>
      </SearchableSelect.Popover>
    </SearchableSelect>
  );
}
